import { ShareholdingDetail, ShareholdingFilingDate, ShareholdingRow } from '../proto/generated/shareholding'

export const PATH_SEP = '/'

const TABLE_SUMMARY_PREFIX = 'Table I - Summary'

/** Matches shareholding ingest sanitize_symbol_for_path — DB symbol key (e.g. M&M → M_M). */
export const toShareholdingDbSymbol = (symbol: string): string => {
    const raw = symbol.trim()
    if (!raw) {
        return ''
    }
    const safe = raw.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^[._]+|[._]+$/g, '')
    return (safe || 'unknown').slice(0, 200).toUpperCase()
}

export const getShareholdingApiSymbol = (input: { code: string } | string): string =>
    toShareholdingDbSymbol(typeof input === 'string' ? input : input.code)

export const PROMOTER_BRANCH = 'Promoter & Promoter Group'
export const PUBLIC_BRANCH = 'Public'
export const INSTITUTIONS_DOMESTIC_BRANCH = `${PUBLIC_BRANCH}${PATH_SEP}Institutions (Domestic)`
export const INSTITUTIONS_FOREIGN_BRANCH = `${PUBLIC_BRANCH}${PATH_SEP}Institutions (Foreign)`
export const NON_INSTITUTIONS_BRANCH = `${PUBLIC_BRANCH}${PATH_SEP}Non Institutions`
export const MUTUAL_FUNDS_BRANCH = `${INSTITUTIONS_DOMESTIC_BRANCH}${PATH_SEP}Mutual Funds`

export const SHAREHOLDING_CATEGORY_COLORS: Record<ShareholdingCategoryId, string> = {
    promoter: '#2E7D32',
    fii: '#E91E8C',
    dii: '#2962FF',
    mf: '#2962FF',
    public: '#FF9800',
    others: '#757575',
}

export type ShareholdingCategoryId = 'promoter' | 'fii' | 'dii' | 'mf' | 'public' | 'others'

export const SUMMARY_TABLE_ROWS: { id: ShareholdingCategoryId; label: string }[] = [
    { id: 'promoter', label: 'Promoter' },
    { id: 'fii', label: 'FII' },
    { id: 'dii', label: 'DII' },
    { id: 'public', label: 'Public' },
    { id: 'others', label: 'Others' },
]

export type ShareholdingQuarterMetrics = {
    percentage: number
    shares: number
    numberOfShareholders?: number
    pledgedPercentage?: number
    lockedPercentage?: number
    votingRightsPercentage?: number
}

export type ShareholdingCategoryTotals = {
    promoter: number
    fii: number
    dii: number
    mf: number
    public: number
    others: number
    pledgedPromoterPct?: number
}

export type ShareholdingTreeNode = {
    key: string
    holderName: string
    holderType: string
    hierarchyPath: string
    depth: number
    isGroup: boolean
    investorCategory?: 'Indian' | 'Foreign'
    children: ShareholdingTreeNode[]
    byQuarter: Record<string, ShareholdingQuarterMetrics>
}

export type ShareholdingEntityHistoryRow = {
    filingDate: string
    periodLabel: string
    shares: number
    percentage: number
    pledgedShares: number
    pledgedPercentage: number
    lockedShares: number
    lockedPercentage: number
}

export type ShareholdingQuarterSnapshot = {
    filingDate: string
    periodLabel: string
    periodRank: number
    rows: ShareholdingRow[]
    summary: ShareholdingCategoryTotals
}

export type ShareholdingDocument = {
    quarters: ShareholdingQuarterSnapshot[]
    periodOrder: string[]
}

const normalize = (value: string) => value.trim().toLowerCase()

export const normalizeHierarchyPath = (path: string): string => {
    let normalized = (path || '').trim()
    if (!normalized || normalized === TABLE_SUMMARY_PREFIX) {
        return ''
    }
    if (normalized.startsWith(`${TABLE_SUMMARY_PREFIX}${PATH_SEP}`)) {
        normalized = normalized.slice(TABLE_SUMMARY_PREFIX.length + PATH_SEP.length)
    } else if (normalized.startsWith(`${TABLE_SUMMARY_PREFIX} -> `)) {
        normalized = normalized.slice(`${TABLE_SUMMARY_PREFIX} -> `.length)
    }
    return normalized.replace(/\s*->\s*/g, PATH_SEP).replace(/\/+/g, PATH_SEP)
}

const lastPathSegment = (path: string) => {
    const parts = normalizeHierarchyPath(path).split(PATH_SEP)
    return parts[parts.length - 1] ?? path
}

export const formatShareholdingPeriodLabel = (filingDate: string): string => {
    const date = new Date(`${filingDate}T00:00:00`)
    if (Number.isNaN(date.getTime())) {
        return filingDate
    }
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

export const formatShareholdingChartAxisLabelLines = (periodLabel: string): string[] => {
    const parts = periodLabel.trim().split(/\s+/)
    if (parts.length >= 2) {
        return [parts[0], parts.slice(1).join(' ')]
    }
    return [periodLabel]
}

export const formatShareholdingPercentage = (value: number | undefined): string => {
    if (value === undefined || Number.isNaN(value)) {
        return '—'
    }
    return `${value.toFixed(2)}%`
}

const formatIndianInteger = (value: number): string => {
    const rounded = Math.round(value)
    const sign = rounded < 0 ? '-' : ''
    const digits = Math.abs(rounded).toString()
    if (digits.length <= 3) {
        return `${sign}${digits}`
    }
    const lastThree = digits.slice(-3)
    const leading = digits.slice(0, -3)
    const groupedLeading = leading.replace(/\B(?=(\d{2})+(?!\d))/g, ',')
    return `${sign}${groupedLeading},${lastThree}`
}

export const formatShareholdingShares = (value: number | undefined): string => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return '—'
    }
    return formatIndianInteger(Number(value))
}

export const formatHolderDisplayName = (name: string): string => {
    const trimmed = (name || '').trim()
    if (!trimmed) {
        return trimmed
    }
    if (trimmed !== trimmed.toLowerCase()) {
        return trimmed
    }
    return trimmed
        .split(/\s+/)
        .map((word) => {
            if (!word) {
                return word
            }
            if (word.length <= 3 && /^[a-z]+$/.test(word)) {
                return word.toUpperCase()
            }
            return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
}

const rowMatchesBranch = (row: ShareholdingRow, branchPath: string): boolean => {
    const path = normalize(normalizeHierarchyPath(row.hierarchyPath))
    const branch = normalize(normalizeHierarchyPath(branchPath))
    const label = normalize(lastPathSegment(branchPath))
    const holder = normalize(row.holderName)

    if (path === branch) {
        return holder === label
    }

    const branchParts = normalizeHierarchyPath(branchPath).split(PATH_SEP)
    const parent = branchParts.length > 1 ? branchParts.slice(0, -1).join(PATH_SEP) : ''

    if (parent && path === normalize(parent) && holder === label) {
        return true
    }

    return false
}

const findBranchPercentage = (rows: ShareholdingRow[], branchPath: string): number => {
    const match = rows.find((row) => rowMatchesBranch(row, branchPath))
    return match?.shareholdingPercentage ?? 0
}

const sumRowsUnderPrefix = (rows: ShareholdingRow[], prefix: string): number => {
    const normalizedPrefix = normalize(normalizeHierarchyPath(prefix))
    const prefixDepth = normalizedPrefix ? normalizedPrefix.split(PATH_SEP).filter(Boolean).length : 0

    const directChildRows = rows.filter((row) => {
        const path = normalizeHierarchyPath(row.hierarchyPath)
        const parts = path.split(PATH_SEP).filter(Boolean)
        if (parts.length !== prefixDepth + 1) {
            return false
        }
        if (prefixDepth === 0) {
            return true
        }
        const rowPrefix = parts.slice(0, prefixDepth).join(PATH_SEP)
        return normalize(rowPrefix) === normalizedPrefix
    })

    if (directChildRows.length) {
        const aggregateRows = directChildRows.filter(
            (row) => normalize(row.holderName) === normalize(lastPathSegment(row.hierarchyPath)),
        )
        const rowsToSum = aggregateRows.length ? aggregateRows : directChildRows
        return rowsToSum.reduce((sum, row) => sum + row.shareholdingPercentage, 0)
    }

    const candidates = rows.filter((row) => {
        const path = normalize(normalizeHierarchyPath(row.hierarchyPath))
        return path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`)
    })

    if (!candidates.length) {
        return 0
    }

    const shallowestDepth = Math.min(
        ...candidates.map((row) => normalizeHierarchyPath(row.hierarchyPath).split(PATH_SEP).length),
    )
    const shallowest = candidates.filter(
        (row) => normalizeHierarchyPath(row.hierarchyPath).split(PATH_SEP).length === shallowestDepth,
    )
    const aggregateRows = shallowest.filter(
        (row) => normalize(row.holderName) === normalize(lastPathSegment(row.hierarchyPath)),
    )
    const rowsToSum = aggregateRows.length ? aggregateRows : shallowest
    return rowsToSum.reduce((sum, row) => sum + row.shareholdingPercentage, 0)
}

export const buildCategoryTotals = (rows: ShareholdingRow[]): ShareholdingCategoryTotals => {
    const promoter = findBranchPercentage(rows, PROMOTER_BRANCH) || sumRowsUnderPrefix(rows, PROMOTER_BRANCH)
    const fii = findBranchPercentage(rows, INSTITUTIONS_FOREIGN_BRANCH) || sumRowsUnderPrefix(rows, INSTITUTIONS_FOREIGN_BRANCH)
    const dii = findBranchPercentage(rows, INSTITUTIONS_DOMESTIC_BRANCH) || sumRowsUnderPrefix(rows, INSTITUTIONS_DOMESTIC_BRANCH)
    const mf = findBranchPercentage(rows, MUTUAL_FUNDS_BRANCH) || sumRowsUnderPrefix(rows, MUTUAL_FUNDS_BRANCH)
    const publicHolding =
        findBranchPercentage(rows, NON_INSTITUTIONS_BRANCH) || sumRowsUnderPrefix(rows, NON_INSTITUTIONS_BRANCH)

    const othersPaths = [
        'Non Promoter-Non Public',
        'Custodian/DR Holder',
        'Employee Benefits Trusts',
    ]
    const others = othersPaths.reduce((sum, branch) => sum + findBranchPercentage(rows, branch), 0)

    const promoterRows = rows.filter((row) =>
        normalize(normalizeHierarchyPath(row.hierarchyPath)).startsWith(`${normalize(PROMOTER_BRANCH)}/`) ||
        normalize(normalizeHierarchyPath(row.hierarchyPath)) === normalize(PROMOTER_BRANCH),
    )
    const pledgedValues = promoterRows
        .map((row) => row.pledgedPercentage)
        .filter((value): value is number => value !== undefined && value > 0)
    const pledgedPromoterPct = pledgedValues.length
        ? pledgedValues.reduce((sum, value) => sum + value, 0)
        : undefined

    return {
        promoter,
        fii,
        dii,
        mf,
        public: publicHolding,
        others,
        pledgedPromoterPct,
    }
}

const toQuarterMetrics = (row: ShareholdingRow): ShareholdingQuarterMetrics => ({
    percentage: row.shareholdingPercentage,
    shares: row.shares,
    numberOfShareholders: row.numberOfShareholders,
    pledgedPercentage: row.pledgedPercentage,
    lockedPercentage: row.lockedPercentage,
    votingRightsPercentage: row.votingRightsPercentage,
})

export const buildShareholdingNodeKey = (row: ShareholdingRow) =>
    `${normalizeHierarchyPath(row.hierarchyPath)}::${row.holderName}`

export const getShareholdingQuarter = (
    document: ShareholdingDocument | undefined,
    filingDate: string,
): ShareholdingQuarterSnapshot | undefined => document?.quarters.find((quarter) => quarter.filingDate === filingDate)

export const getShareholdingQuarterSelectOptions = (document: ShareholdingDocument) =>
    document.periodOrder.map((filingDate) => ({
        value: filingDate,
        label: document.quarters.find((entry) => entry.filingDate === filingDate)?.periodLabel ?? filingDate,
    }))

export const buildShareholdingEntityLabelMap = (tree: ShareholdingTreeNode[]): Map<string, string> => {
    const map = new Map<string, string>()
    const walk = (nodes: ShareholdingTreeNode[]) => {
        nodes.forEach((node) => {
            map.set(node.key, formatHolderDisplayName(node.holderName))
            walk(node.children)
        })
    }
    walk(tree)
    return map
}

export const findShareholdingTreeNode = (
    tree: ShareholdingTreeNode[],
    nodeKey: string,
): ShareholdingTreeNode | undefined => {
    for (const node of tree) {
        if (node.key === nodeKey) {
            return node
        }
        const found = findShareholdingTreeNode(node.children, nodeKey)
        if (found) {
            return found
        }
    }
    return undefined
}

const getDirectChildHierarchyPath = (node: ShareholdingTreeNode): string => {
    const parentPath = normalizeHierarchyPath(node.hierarchyPath)
    if (!parentPath) {
        return node.holderName
    }
    return `${parentPath}${PATH_SEP}${node.holderName}`
}

export const entityHasChartableDataInQuarter = (
    document: ShareholdingDocument,
    entityKey: string,
    filingDate: string,
): boolean => {
    const quarter = document.quarters.find((entry) => entry.filingDate === filingDate)
    const row = quarter?.rows.find((entry) => buildShareholdingNodeKey(entry) === entityKey)
    return (row?.shareholdingPercentage ?? 0) > 0
}

export const filterShareholdingTreeByQuarter = (
    nodes: ShareholdingTreeNode[],
    filingDate: string,
): ShareholdingTreeNode[] =>
    nodes
        .map((node) => {
            const children = filterShareholdingTreeByQuarter(node.children, filingDate)
            const percentage = node.byQuarter[filingDate]?.percentage ?? 0
            if (percentage <= 0 && !children.length) {
                return undefined
            }
            return {
                ...node,
                children,
                isGroup: children.length > 0,
            }
        })
        .filter((node): node is ShareholdingTreeNode => Boolean(node))

export const rowUnderBranch = (row: ShareholdingRow, branchPath: string): boolean => {
    const path = normalize(normalizeHierarchyPath(row.hierarchyPath))
    const branch = normalize(normalizeHierarchyPath(branchPath))
    if (!path || !branch) {
        return false
    }
    return path === branch || path.startsWith(`${branch}/`)
}

export const buildShareholdingTree = (
    quarterDetails: ShareholdingDetail[],
    branchFilter?: string | string[],
): ShareholdingTreeNode[] => {
    const branchFilters = branchFilter ? (Array.isArray(branchFilter) ? branchFilter : [branchFilter]) : undefined
    const matchesBranch = (row: ShareholdingRow) =>
        !branchFilters?.length || branchFilters.some((branch) => rowUnderBranch(row, branch))

    const nodeMap = new Map<string, ShareholdingTreeNode>()

    quarterDetails.forEach((detail) => {
        detail.rows.forEach((row) => {
            if (!matchesBranch(row)) {
                return
            }
            if (row.shareholdingPercentage <= 0 && !row.numberOfShareholders) {
                return
            }

            const hierarchyPath = normalizeHierarchyPath(row.hierarchyPath)
            const key = buildShareholdingNodeKey(row)
            const existing = nodeMap.get(key)
            const metrics = toQuarterMetrics(row)

            if (existing) {
                existing.byQuarter[detail.filingDate] = metrics
                return
            }

            nodeMap.set(key, {
                key,
                holderName: row.holderName,
                holderType: row.holderType,
                hierarchyPath,
                depth: 0,
                isGroup: false,
                children: [],
                byQuarter: { [detail.filingDate]: metrics },
            })
        })
    })

    const nodes = Array.from(nodeMap.values()).sort((a, b) => {
        const depthDiff = a.hierarchyPath.split(PATH_SEP).length - b.hierarchyPath.split(PATH_SEP).length
        if (depthDiff !== 0) {
            return depthDiff
        }
        if (a.hierarchyPath !== b.hierarchyPath) {
            return a.hierarchyPath.localeCompare(b.hierarchyPath)
        }
        return a.holderName.localeCompare(b.holderName)
    })

    const findParentNode = (node: ShareholdingTreeNode): ShareholdingTreeNode | undefined => {
        const childPath = normalize(normalizeHierarchyPath(node.hierarchyPath))

        let bestParent: ShareholdingTreeNode | undefined

        nodes.forEach((candidate) => {
            if (candidate.key === node.key) {
                return
            }
            const expectedChildPath = normalize(getDirectChildHierarchyPath(candidate))
            if (childPath === expectedChildPath) {
                bestParent = candidate
            }
        })

        return bestParent
    }

    const roots: ShareholdingTreeNode[] = []

    nodes.forEach((node) => {
        const parent = findParentNode(node)
        if (parent) {
            parent.isGroup = true
            if (!parent.children.some((child) => child.key === node.key)) {
                parent.children.push(node)
                parent.children.sort((a, b) => a.holderName.localeCompare(b.holderName))
            }
            return
        }
        roots.push(node)
    })

    const assignDepth = (treeNodes: ShareholdingTreeNode[], depth: number) => {
        treeNodes.forEach((node) => {
            node.depth = depth
            if (node.children.length) {
                node.isGroup = true
                assignDepth(node.children, depth + 1)
            }
        })
    }

    assignDepth(roots, 0)

    return roots.sort((a, b) => a.hierarchyPath.localeCompare(b.hierarchyPath) || a.holderName.localeCompare(b.holderName))
}

export const hasShareholdingEntitySectionContent = (hasCharts: boolean, tree: ShareholdingTreeNode[]): boolean =>
    hasCharts || tree.length > 0

export const getPromoterInvestorCategory = (hierarchyPath: string): 'Indian' | 'Foreign' | undefined => {
    const path = normalizeHierarchyPath(hierarchyPath)
    const parts = path.split(PATH_SEP)
    const nationalityPart = parts.find((part) => {
        const normalized = normalize(part)
        return normalized === 'indian' || normalized === 'foreign'
    })
    if (!nationalityPart) {
        return undefined
    }
    return normalize(nationalityPart) === 'indian' ? 'Indian' : 'Foreign'
}

export const isPromoterNationalityAggregateNode = (node: ShareholdingTreeNode): boolean => {
    const name = normalize(node.holderName)
    return name === 'indian' || name === 'foreign'
}

export const isPromoterRootAggregateNode = (node: ShareholdingTreeNode): boolean => {
    const path = normalize(normalizeHierarchyPath(node.hierarchyPath))
    const name = normalize(node.holderName)
    return (
        path === normalize(PROMOTER_BRANCH) &&
        (name === normalize(PROMOTER_BRANCH) ||
            name === normalize('promoter & promoter group') ||
            name === 'indian' ||
            name === 'foreign')
    )
}

export const flattenPromoterTree = (
    nodes: ShareholdingTreeNode[],
    inheritedCategory?: 'Indian' | 'Foreign',
    depth = 0,
): ShareholdingTreeNode[] => {
    const result: ShareholdingTreeNode[] = []

    nodes.forEach((node) => {
        if (isPromoterRootAggregateNode(node) || isPromoterNationalityAggregateNode(node)) {
            const category = isPromoterNationalityAggregateNode(node)
                ? normalize(node.holderName) === 'indian'
                    ? 'Indian'
                    : 'Foreign'
                : inheritedCategory
            result.push(...flattenPromoterTree(node.children, category, depth))
            return
        }

        const category = inheritedCategory ?? getPromoterInvestorCategory(node.hierarchyPath)
        const children = flattenPromoterTree(node.children, category, depth + 1)
        result.push({
            ...node,
            depth,
            investorCategory: category,
            children,
            isGroup: children.length > 0,
        })
    })

    return result
}

export const buildPromoterTree = (
    quarterDetails: ShareholdingDetail[],
    activeQuarter?: string,
): ShareholdingTreeNode[] => {
    const raw = buildShareholdingTree(quarterDetails, PROMOTER_BRANCH)
    const flattened = flattenPromoterTree(raw)
    return activeQuarter ? filterShareholdingTreeByQuarter(flattened, activeQuarter) : flattened
}

export const buildInstitutionalTree = (
    quarterDetails: ShareholdingDetail[],
    activeQuarter?: string,
): ShareholdingTreeNode[] => {
    const raw = buildShareholdingTree(quarterDetails, [
        INSTITUTIONS_DOMESTIC_BRANCH,
        INSTITUTIONS_FOREIGN_BRANCH,
    ])
    return activeQuarter ? filterShareholdingTreeByQuarter(raw, activeQuarter) : raw
}

export const buildPublicTree = (
    quarterDetails: ShareholdingDetail[],
    activeQuarter?: string,
): ShareholdingTreeNode[] => {
    const raw = buildShareholdingTree(quarterDetails, NON_INSTITUTIONS_BRANCH)
    return activeQuarter ? filterShareholdingTreeByQuarter(raw, activeQuarter) : raw
}

export const getTreeNodeQuarterPercentage = (
    tree: ShareholdingTreeNode[],
    nodeKey: string,
    quarter: string,
): number => {
    const walk = (nodes: ShareholdingTreeNode[]): number | undefined => {
        for (const node of nodes) {
            if (node.key === nodeKey) {
                return node.byQuarter[quarter]?.percentage ?? 0
            }
            const found = walk(node.children)
            if (found !== undefined) {
                return found
            }
        }
        return undefined
    }
    return walk(tree) ?? 0
}

/** Sum top-level promoter category rows for the active quarter (matches table totals). */
export const getPromoterTreeQuarterTotal = (tree: ShareholdingTreeNode[], quarter: string): number =>
    tree.reduce((sum, node) => sum + (node.byQuarter[quarter]?.percentage ?? 0), 0)

export const resolvePromoterQuarterTotal = (
    document: ShareholdingDocument,
    tree: ShareholdingTreeNode[],
    quarter: string,
): number => {
    const fromSummary = getShareholdingQuarter(document, quarter)?.summary.promoter ?? 0
    const fromTree = getPromoterTreeQuarterTotal(tree, quarter)
    return Math.max(fromSummary, fromTree)
}

export const resolveDefaultLeafChartEntities = (
    document: ShareholdingDocument,
    tree: ShareholdingTreeNode[],
    selectedQuarter: string,
): string[] => {
    const leaves: ShareholdingTreeNode[] = []
    const walk = (nodes: ShareholdingTreeNode[]) => {
        nodes.forEach((node) => {
            if (!node.children.length) {
                leaves.push(node)
                return
            }
            walk(node.children)
        })
    }
    walk(tree)

    return leaves
        .filter((node) => entityHasChartableDataInQuarter(document, node.key, selectedQuarter))
        .sort(
            (a, b) =>
                (b.byQuarter[selectedQuarter]?.percentage ?? 0) -
                (a.byQuarter[selectedQuarter]?.percentage ?? 0),
        )
        .slice(0, 2)
        .map((node) => node.key)
}

export const buildShareholdingDocument = (
    details: ShareholdingDetail[],
    filingDates: ShareholdingFilingDate[] = [],
): ShareholdingDocument => {
    const sortedDetails = [...details].sort((a, b) => a.periodRank - b.periodRank)
    const periodOrder = filingDates.length
        ? filingDates.map((entry) => entry.filingDate)
        : sortedDetails.map((detail) => detail.filingDate)

    const quarters = periodOrder
        .map((filingDate) => sortedDetails.find((detail) => detail.filingDate === filingDate))
        .filter((detail): detail is ShareholdingDetail => Boolean(detail))
        .map((detail) => ({
            filingDate: detail.filingDate,
            periodLabel: formatShareholdingPeriodLabel(detail.filingDate),
            periodRank: detail.periodRank,
            rows: detail.rows ?? [],
            summary: buildCategoryTotals(detail.rows ?? []),
        }))

    return { quarters, periodOrder }
}

export const getCategoryValue = (summary: ShareholdingCategoryTotals, category: ShareholdingCategoryId): number =>
    summary[category]

export const isShareholdingEmpty = (document: ShareholdingDocument | undefined): boolean =>
    !document?.quarters?.length

export const toShareholdingQuarterDetails = (document: ShareholdingDocument): ShareholdingDetail[] =>
    document.quarters.map((quarter) => ({
        exchange: 0,
        symbol: '',
        filingDate: quarter.filingDate,
        periodRank: quarter.periodRank,
        rows: quarter.rows,
    }))

export const compareShareholdingQuartersNewestFirst = (
    a: { filingDate: string; periodRank?: number },
    b: { filingDate: string; periodRank?: number },
    periodOrder: string[] = [],
): number => {
    if (periodOrder.length) {
        const indexA = periodOrder.indexOf(a.filingDate)
        const indexB = periodOrder.indexOf(b.filingDate)
        if (indexA !== -1 && indexB !== -1 && indexA !== indexB) {
            return indexA - indexB
        }
    }
    const rankDiff = (a.periodRank ?? 0) - (b.periodRank ?? 0)
    if (rankDiff !== 0) {
        return rankDiff
    }
    return b.filingDate.localeCompare(a.filingDate)
}

export const getEntityFullHistory = (
    document: ShareholdingDocument,
    entityKey: string,
): ShareholdingEntityHistoryRow[] =>
    [...document.quarters]
        .sort((a, b) => compareShareholdingQuartersNewestFirst(a, b, document.periodOrder))
        .map((quarter) => {
            const row = quarter.rows.find((entry) => buildShareholdingNodeKey(entry) === entityKey)
            return {
                filingDate: quarter.filingDate,
                periodLabel: quarter.periodLabel,
                shares: row?.shares ?? 0,
                percentage: row?.shareholdingPercentage ?? 0,
                pledgedShares: row?.pledgedShares ?? 0,
                pledgedPercentage: row?.pledgedPercentage ?? 0,
                lockedShares: row?.lockedShares ?? 0,
                lockedPercentage: row?.lockedPercentage ?? 0,
            }
        })

export const getHolderHistoryInStock = (
    document: ShareholdingDocument,
    holderName: string,
    hierarchyPath?: string,
): ShareholdingEntityHistoryRow[] => {
    const normalizedHolder = normalize(holderName)
    const normalizedPath = hierarchyPath ? normalizeHierarchyPath(hierarchyPath) : undefined

    return [...document.quarters]
        .sort((a, b) => compareShareholdingQuartersNewestFirst(a, b, document.periodOrder))
        .map((quarter) => {
            const matchingRows = quarter.rows.filter((row) => {
                if (normalize(row.holderName) !== normalizedHolder) {
                    return false
                }
                if (normalizedPath) {
                    return normalizeHierarchyPath(row.hierarchyPath) === normalizedPath
                }
                return true
            })
            const row = matchingRows.sort((a, b) => b.shares - a.shares)[0]
            return {
                filingDate: quarter.filingDate,
                periodLabel: quarter.periodLabel,
                shares: row?.shares ?? 0,
                percentage: row?.shareholdingPercentage ?? 0,
                pledgedShares: row?.pledgedShares ?? 0,
                pledgedPercentage: row?.pledgedPercentage ?? 0,
                lockedShares: row?.lockedShares ?? 0,
                lockedPercentage: row?.lockedPercentage ?? 0,
            }
        })
}
